import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, ArrowUpDown, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Импортируем функции из нового API
import { getColumnByKey, getColumnDescription } from './columnsAPI';

// Компонент тултипа, который показывает описание колонки
const Tooltip = ({ content, position }) => {
  if (!position) return null;

  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: position.y - 10,
        left: position.x,
        transform: 'translate(-50%, -100%)',
      }}
      className="bg-gray-900 text-white rounded-lg shadow-lg p-3 text-sm max-w-xs z-[9999]"
    >
      {content}
      <div 
        className="absolute left-1/2 -bottom-1 w-2 h-2 bg-gray-900 transform -translate-x-1/2 rotate-45"
      />
    </div>,
    document.body
  );
};

// Вспомогательная функция для получения иконки сортировки
const getSortIcon = (columnKey, sortConfig) => {
  if (!sortConfig || sortConfig.key !== columnKey) return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  if (sortConfig.direction === 'asc') return <ArrowUp className="w-4 h-4" />;
  return <ArrowDown className="w-4 h-4" />;
};

// Fallback описания для стандартных колонок (на случай отсутствия в метаданных)
const FALLBACK_DESCRIPTIONS = {
  date: "Date when statistics were collected",
  page_loads: "Number of unique visits to the landing page",
  sms_success: "Number of successfully sent SMS validation codes", 
  registration_success: "Number of users who successfully completed registration",
  sms_errors: "Count of failed SMS validation attempts",
  registration_errors: "Count of failed registration attempts after SMS validation",
  sms_errors_percentage: "Percentage of SMS errors from total SMS attempts",
  registration_errors_percentage: "Percentage of registration errors from total registration attempts", 
  success_rate: "Percentage of successful registrations from successful SMS validations",
  ratio: "Percentage of successful registrations from total page loads",
  registration_unfinished_sms: "Users who started but didn't complete SMS validation",
  registration_unfinished_form: "Users who completed SMS but didn't finish registration form",
  registration_unfinished: "Total users who started but didn't complete registration process",
  registration_unfinished_sms_percentage: "Percentage of users who didn't complete SMS validation from total registration attempts",
  registration_unfinished_form_percentage: "Percentage of users who didn't complete form from total registration attempts", 
  registration_unfinished_percentage: "Percentage of incomplete registrations from total registration attempts"
};

const SortableTableHeader = ({ 
  id, 
  label, 
  onSort, 
  sortConfig, 
  isLast = false,
  columnsMetadata = [], // Получаем метаданные от родителя
  ...props
}) => {
  const [tooltipPosition, setTooltipPosition] = useState(null);
  
  // Получаем описание из метаданных или используем fallback
  const getTooltipContent = () => {
    // Сначала пробуем получить из метаданных
    const description = getColumnDescription(id, columnsMetadata);
    if (description) {
      return description;
    }
    
    // Если нет в метаданных, используем fallback
    const fallbackDescription = FALLBACK_DESCRIPTIONS[id];
    if (fallbackDescription) {
      return fallbackDescription;
    }
    
    // Если и fallback нет, возвращаем null (тултип не показываем)
    return null;
  };
  
  const tooltipContent = getTooltipContent();
  const hasTooltip = !!tooltipContent;
  
  // Важно: преобразуем id в строку для корректной работы drag-and-drop
  const sortableId = String(id);
  
  const { 
    attributes, 
    listeners, 
    setNodeRef, 
    transform, 
    transition, 
    isDragging 
  } = useSortable({
    id: sortableId,
    // Отключаем автоматическое определение обработчика нажатия
    // Будем использовать его только для специальной "ручки"
    disabled: { keyboard: false }
  });
  
  // Логирование для отладки
  useEffect(() => {
    if (typeof id !== 'string' && typeof id !== 'number') {
      console.warn(`SortableTableHeader получил id типа ${typeof id}`);
    }
  }, [id]);
  
  // Закрываем тултип при скролле
  useEffect(() => {
    const handleScroll = () => setTooltipPosition(null);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const handleSortClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onSort) {
      onSort(id);
    }
  };

  const handleTooltipShow = (e) => {
    if (!hasTooltip) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.x + rect.width / 2,
      y: rect.y
    });
  };

  const handleTooltipHide = () => {
    setTooltipPosition(null);
  };

  // Определяем тип колонки для дополнительной информации
  const getColumnTypeInfo = () => {
    const column = getColumnByKey(id, columnsMetadata);
    if (!column) return null;
    
    const typeLabels = {
      'base': 'Base Metric',
      'calculated': 'Calculated',
      'percentage_ratio': 'Percentage',
      'percentage_attempts': 'Percentage',
      'error_category': 'Error Category'
    };
    
    return typeLabels[column.column_type] || null;
  };

  const columnTypeInfo = getColumnTypeInfo();

  return (
    <th 
      ref={setNodeRef} 
      className={`
        px-6 py-3 
        border-b border-r border-gray-200 last:border-r-0
        transition-all duration-150
        ${isDragging ? 'bg-transparent' : 'bg-gray-50'}
        select-none
      `}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : 'auto'
      }}
      data-column-id={id} // Добавляем data-атрибут для идентификации колонки
      {...props} // Распространяем остальные пропсы
    >
      <div className="flex items-center justify-between">
        {/* Заголовок колонки */}
        <span 
          className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer"
          onClick={handleSortClick}
        >
          {label}
          {/* Показываем тип колонки для системных колонок */}
          {columnTypeInfo && (
            <span className="text-[10px] text-gray-400 ml-1 normal-case">
              ({columnTypeInfo})
            </span>
          )}
        </span>
        
        <div className="flex items-center gap-2">
          {/* Иконка сортировки как отдельный кликабельный элемент */}
          {onSort && (
            <div 
              className="cursor-pointer p-1 hover:bg-gray-200 rounded transition-colors"
              onClick={handleSortClick}
              title="Sort column"
            >
              {getSortIcon(id, sortConfig)}
            </div>
          )}
          
          {/* Иконка подсказки */}
          {hasTooltip && (
            <div 
              className="inline-flex items-center justify-center cursor-help transition-opacity hover:opacity-80" 
              onClick={e => e.stopPropagation()}
              onMouseEnter={handleTooltipShow}
              onMouseLeave={handleTooltipHide}
              title="Column description"
            >
              <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
            </div>
          )}
          
          {/* Ручка для перетаскивания */}
          <div 
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded transition-colors"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
          >
            <GripVertical className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors" />
          </div>
        </div>
      </div>
      
      {/* Тултип с расширенной информацией */}
      {hasTooltip && tooltipPosition && (
        <Tooltip 
          content={
            <div>
              <div className="font-medium mb-1">{label}</div>
              <div className="text-gray-300">{tooltipContent}</div>
              {columnTypeInfo && (
                <div className="text-gray-400 text-xs mt-2 border-t border-gray-700 pt-1">
                  Type: {columnTypeInfo}
                </div>
              )}
            </div>
          }
          position={tooltipPosition}
        />
      )}
    </th>
  );
};

export default SortableTableHeader;